import { Avatar, Button, Stack, Typography } from '@mui/material';
import {
  ISubject,
  ISubCategory,
  IDepartment,
} from 'API/UniversalRequest/universalRequest';
import { TAB_VALUE_TYPE } from '..';
import { SubCategoriesListItem } from './SubCategoriesListItem';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { BackButtonArrow } from 'Images';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CustomMetaTitle, HiddenAccessibilityText } from 'Components';

interface ISubCategoriesList {
  categoriesList: {
    subjects: ISubject[];
    departments: IDepartment[];
    subSubjects: ISubCategory[];
  };
  tabValue: TAB_VALUE_TYPE;
  selectedCategoryId: number | null;
  handleSubCategoryClick: (subCategoryId: number) => void;
  goBack: (isReset?: boolean) => void;
  getParentTitleById: string;
  isLoading: boolean;
}

export const SubCategoriesList = ({
  categoriesList,
  tabValue,
  selectedCategoryId,
  handleSubCategoryClick,
  goBack,
  getParentTitleById,
  isLoading,
}: ISubCategoriesList) => {
  const [filteredSubCategories, setFilteredSubCategories] = useState<
    ISubCategory[] | ISubject[]
  >([]);
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const isSubject = tabValue === TAB_VALUE_TYPE.SUBJECTS;

  useEffect(() => {
    if (isSubject) {
      const newFilteredSubCategories: ISubCategory[] = [];
      categoriesList.subjects
        .find((subject: ISubject) => subject.id === selectedCategoryId)
        ?.subSubjectIds.forEach((subSubjectId) => {
          const subSubject = categoriesList.subSubjects.find(
            (subSubject: ISubCategory) => subSubject.id === subSubjectId
          );

          if (subSubject) newFilteredSubCategories.push(subSubject);
        });
      if (!isLoading && newFilteredSubCategories.length === 0) goBack(true);
      else setFilteredSubCategories(newFilteredSubCategories);
    } else {
      const newFilteredSubCategories =
        categoriesList.departments.find(
          (department) => department.id === selectedCategoryId
        )?.parentCatagories || [];
      if (!isLoading && newFilteredSubCategories.length === 0) goBack(true);
      else setFilteredSubCategories(newFilteredSubCategories);
    }
  }, [
    categoriesList.departments,
    categoriesList.subSubjects,
    categoriesList.subjects,
    isSubject,
    selectedCategoryId,
  ]);
  const params = useParams();

  useEffect(() => {
    if (!!params.subCategoryId) {
      handleSubCategoryClick(parseInt(params.subCategoryId));
    }
  }, [handleSubCategoryClick, params.subCategoryId]);

  useEffect(() => {
    if (isSubject || !!selectedCategoryId) window.scrollTo(0, 0);
    else goBack(true);
  }, []);

  return (
    <>
      <CustomMetaTitle
        title={`${dictionary.title} ${dictionary.subject} ${getParentTitleById} - ${dictionary.metaTitleStepTwo}`}
        disablePrefix
      />
      <Stack
        maxWidth='680px'
        m='64px auto 0'
        p={isDesktop ? '32px 90px 48px' : '0 24px'}
        bgcolor={isDesktop ? '#E7EDFF' : undefined}
        border={isDesktop ? '1px solid #B7CCFF' : undefined}
        borderRadius='20px'
        gap='12px'
        component='fieldset'
        aria-describedby='select-parent-description'
      >
        {isSubject ? (
          <>
            <legend>{dictionary.inWhatMatter}</legend>
            <span className='sr-only' id='select-parent-description'>
              {dictionary.selectParentDescription}
            </span>
            <Typography variant={'body1'} mb='4px' component='h2'>
              {dictionary.inWhatMatter}
            </Typography>
            {filteredSubCategories?.length > 0 ? (
              filteredSubCategories.map((subSubject) => (
                <SubCategoriesListItem
                  key={subSubject.id}
                  subCategory={subSubject as ISubCategory}
                  handleSubCategoryClick={handleSubCategoryClick}
                />
              ))
            ) : (
              <Typography>
                {dictionary.noAvailableDisabilityConfirmations}
              </Typography>
            )}
          </>
        ) : (
          (filteredSubCategories as ISubject[])?.map((subject) => (
            <>
              <Typography mt='34px' component='h2'>
                {subject.title}
              </Typography>
              {subject.subSubjectIds.map((subSubjectId) => (
                <SubCategoriesListItem
                  key={subSubjectId}
                  subCategory={
                    categoriesList.subSubjects.find(
                      (subSubject) => subSubject.id === subSubjectId
                    ) as ISubCategory
                  }
                  handleSubCategoryClick={handleSubCategoryClick}
                />
              ))}
            </>
          ))
        )}
        <Button
          variant='outlined'
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: '12px 0',
            width: isDesktop ? '234px' : '208px',
            m: '52px auto 0',
            borderColor: 'primary.main',
            bgcolor: '#fff',
            '&:hover': {
              bgcolor: '#fff',
            },
          }}
          onClick={() => goBack()}
        >
          <Avatar
            src={BackButtonArrow}
            alt={''}
            sx={{
              width: isDesktop ? '20px' : '16px',
              height: isDesktop ? '20px' : '16px',
            }}
          />
          <Typography variant={'body4'} color={'primary.main'}>
            {Dictionary[DICTIONARY.General].back}
          </Typography>
        </Button>
      </Stack>
    </>
  );
};
